import { useEffect, useMemo, useState } from "react"
import EvaluateResults from "../../Components/EvaluateResults/EvaluateResults"
import { getAbstractScreeningStatus } from "../../Utils/functions"
import { useLiteratureReview } from "../../Context/LiteratureReviewContext"

const EvaluateScreening = () => {
    const { id, workflowData, setWorkflowData, navigateToStep, setFromStep, getStepsForPage } = useLiteratureReview()

    const steps = getStepsForPage("recommended-abstracts")

    const [fetchedScreenedArticles, setFetchedScreenedArticles] = useState(null)
    const [fetchedArticleUniqueIds, setFetchedArticleUniqueIds] = useState(null)
    const [fetchedArticleValues, setFetchedArticleValues] = useState(null)
    const [fetchedSuggestedColumns, setFetchedSuggestedColumns] = useState(null)

    useEffect(() => {
        if (!id) return
        let cancelled = false
        const load = async () => {
            try {
                const data = await getAbstractScreeningStatus(id)
                if (cancelled) return
                if (Array.isArray(data?.addedArticles)) setFetchedScreenedArticles(data.addedArticles)
                if (Array.isArray(data?.articleUniqueIds)) setFetchedArticleUniqueIds(data.articleUniqueIds)
                if (data?.articleValues && typeof data.articleValues === "object") setFetchedArticleValues(data.articleValues)
                if (Array.isArray(data?.suggestedColumns)) setFetchedSuggestedColumns(data.suggestedColumns)
            } catch (err) {
                console.error("Failed to fetch abstract screening data:", err)
            }
        }
        load()
        return () => { cancelled = true }
    }, [id])

    // API data takes priority; fall back to context workflowData (set by AbstractScreening)
    const screenedArticles = Array.isArray(fetchedScreenedArticles) && fetchedScreenedArticles.length > 0
        ? fetchedScreenedArticles
        : workflowData.screenedArticles
    const articleUniqueIds = Array.isArray(fetchedArticleUniqueIds) && fetchedArticleUniqueIds.length > 0
        ? fetchedArticleUniqueIds
        : workflowData.articleUniqueIds
    const articleValues = fetchedArticleValues && typeof fetchedArticleValues === "object" && Object.keys(fetchedArticleValues).length > 0
        ? fetchedArticleValues
        : workflowData.articleValues
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const suggestedColumns = useMemo(() =>
        Array.isArray(fetchedSuggestedColumns) && fetchedSuggestedColumns.length > 0
            ? fetchedSuggestedColumns
            : workflowData.suggestedColumns,
        [fetchedSuggestedColumns, workflowData.suggestedColumns]
    )

    const handleBack = () => {
        navigateToStep("abstract-screening")
    }

    const handleNext = (includedIds, includedScreenedArticles, includedArticleValues) => {
        setWorkflowData({
            screenedArticles: includedScreenedArticles,
            articleUniqueIds: includedIds,
            articleValues: includedArticleValues,
        })
        navigateToStep("full-text-screening")
    }

    const handleDefineExtraction = (includedIds, includedScreenedArticles, includedArticleValues) => {
        setWorkflowData({
            articleUniqueIds: includedIds,
            screenedArticles: includedScreenedArticles,
            articleValues: includedArticleValues,
        })
        setFromStep("recommended-abstracts")
        navigateToStep("data-extraction-definition")
    }

    return (
        <EvaluateResults
            projectTitle={workflowData.litreviewtitle}
            screenedArticles={screenedArticles}
            articleUniqueIds={articleUniqueIds}
            articleValues={articleValues}
            suggestedColumns={suggestedColumns}
            screeningType="abstract"
            steps={steps}
            onBack={handleBack}
            onNext={handleNext}
            nextLabel="Screen full-texts"
            showBack={true}
            secondaryButtonLabel="Extract data"
            onSecondaryClick={handleDefineExtraction}
            showSecondary={true}
        />
    )
}

export default EvaluateScreening
